import { forgetCodeLabels, useCodeLabels } from "../codes";
import { ClientAliasesPanel } from "./Clients";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Badge,
  Body1,
  Button,
  Caption1,
  Card,
  Field,
  Input,
  MessageBar,
  MessageBarBody,
  Select,
  Subtitle2,
  Switch,
  Tab,
  TabList,
  Textarea,
  Tooltip,
} from "@fluentui/react-components";
import {
  DEFAULT_PROBABILITY,
  estimatePlan,
  EXPENSE_CATEGORIES,
  EXPENSE_LABEL,
  isClosed,
  mondayOf,
  monthOf,
  newId,
  planEnd,
  ratesForPlan,
  resizePlan,
  REVENUE_TYPE_LABEL,
  REVENUE_TYPES,
  STATUS_LABEL,
  type CostBasis,
  type PlanExpense,
  type Project,
  type ScheduleLine,
  type WorkingPlan,
  type Zoom,
  applyRange,
  coverageGaps,
  type RangeOp,
  MAX_WEEKS,
  DEFAULT_THRESHOLDS,
  contractValue,
  coverCount,
  expandTiers,
  isIrregular,
  regularized,
  withCover,
  withFrequency,
  today,
  invoiceDayLabel,
  ordinal,
  resolveInvoiceDay,
  scheduledInvoices,
  type ClientBilling,
  type RevenueType,
} from "../../../core";
import { errorText, send, useApi, type CodesResponse, type LoadResponse, type ProjectResponse, type ProjectsResponse } from "../api";
import { DateInput } from "../DateInput";
import { InvoiceDaySelect } from "../InvoiceDaySelect";
import { MOCK } from "../config";
import { day, money, pct, when } from "../format";
import { go, href } from "../router";
import { ErrorBar, Kpi, Loading, Page, useMe, Warnings } from "../ui";
import { AllocationGrid, type Block, type GridEdit } from "./AllocationGrid";
import { ProjectTracking } from "./ProjectTracking";
import { BillingCard } from "./Expenses";
import { CashChart, MarginChart, MonthTable } from "./Charts";
import { AssignmentDialog, CopyDialog, DeleteDialog, OwnerDialog, RangeDialog, StatusDialog } from "./ProjectDialogs";
import { Switcher } from "./Switcher";

/** Jump to another project (rev. 1.6). Leaving with unsaved changes still asks first. */
function ProjectSwitcher({ current }: { current: string }) {
  const { label: codeLabel } = useCodeLabels();
  const list = useApi<ProjectsResponse>("/projects");
  const items = (list.data?.projects ?? []).map((p) => ({
    id: p.id,
    text: p.isTemplate ? p.name : `${p.code ? codeLabel(p.code) : "No code"} — ${p.name}`,
    group: p.isTemplate ? "Templates" : isClosed(p.status) ? "Closed" : "Open",
  }));
  return <Switcher label="Go to another project" items={items} current={current} onPick={(pid) => go("projects", pid)} />;
}

function BackRow({ id }: { id: string }) {
  return (
    <div className="page-nav">
      <a href={href("projects")} className="back">
        ← Projects
      </a>
      <ProjectSwitcher current={id} />
    </div>
  );
}

/** What the editor changes: the project's details and its working plan. Status changes go through their own dialog. */
export type Draft = Pick<Project, "code" | "name" | "clientName" | "billable" | "isTemplate" | "offering" | "notes" | "targetMargin" | "contractDate" | "contractStart" | "contractEnd"> & { plan: WorkingPlan };

const HISTORY = 50;

/** Undo and redo over whole drafts (PE5: at least 50 steps). Typing in one field coalesces into one step. */
function useHistory(initial: Draft | null) {
  const [h, setH] = useState<{ past: Draft[]; present: Draft | null; future: Draft[]; key?: string }>({ past: [], present: initial, future: [] });
  const set = useCallback((next: Draft | ((d: Draft) => Draft), key?: string) => {
    setH((s) => {
      if (!s.present) return s;
      const value = typeof next === "function" ? next(s.present) : next;
      if (key && key === s.key) return { ...s, present: value, future: [] };
      return { past: [...s.past, s.present].slice(-HISTORY), present: value, future: [], key };
    });
  }, []);
  const undo = useCallback(() => setH((s) => (s.past.length && s.present ? { past: s.past.slice(0, -1), present: s.past.at(-1)!, future: [s.present, ...s.future] } : s)), []);
  const redo = useCallback(() => setH((s) => (s.future.length && s.present ? { past: [...s.past, s.present], present: s.future[0], future: s.future.slice(1) } : s)), []);
  const reset = useCallback((value: Draft) => setH({ past: [], present: value, future: [] }), []);
  return { draft: h.present, set, undo, redo, reset, canUndo: h.past.length > 0, canRedo: h.future.length > 0 };
}

const draftOf = (p: Project): Draft => ({
  code: p.code,
  name: p.name,
  clientName: p.clientName,
  billable: p.billable,
  isTemplate: p.isTemplate,
  offering: p.offering,
  notes: p.notes,
  targetMargin: p.targetMargin,
  contractDate: p.contractDate,
  contractStart: p.contractStart,
  contractEnd: p.contractEnd,
  plan: p.plan,
});

type Dialog = { kind: "assignment"; row: number | null } | { kind: "range" } | { kind: "status" } | { kind: "owner" } | { kind: "copy" } | { kind: "renewal" } | { kind: "delete" } | null;

export function ProjectEditor({ id }: { id: string }) {
  const { label: codeLabel } = useCodeLabels();
  const me = useMe();
  const data = useApi<ProjectResponse>(`/projects/${encodeURIComponent(id)}`);
  const codes = useApi<CodesResponse>("/project-codes");
  const history = useHistory(null);
  const { draft, set } = history;
  const [etag, setEtag] = useState<string>();
  const [savedJson, setSavedJson] = useState("");
  const [zoom, setZoom] = useState<Zoom>("week");
  const [basis, setBasis] = useState<CostBasis>("base");
  const [burdened, setBurdened] = useState(false);
  const [block, setBlock] = useState<Block | null>(null);
  const [dialog, setDialog] = useState<Dialog>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>();
  const [showTable, setShowTable] = useState(false);
  const [converted, setConverted] = useState<string[]>([]);

  // Lines entered before rev. 1.7 ("monthly, 1 time, each covering 8 months") are read the way the
  // schedule is entered now: the same amount per period, across the same months. The draft is left
  // unsaved so the change is seen, and kept only when someone saves it.
  useEffect(() => {
    if (!data.data) return;
    const d = draftOf(data.data.project);
    const old = d.plan.schedule.filter(isIrregular);
    const next = old.length ? { ...d, plan: { ...d.plan, schedule: d.plan.schedule.map(regularized) } } : d;
    history.reset(next);
    setEtag(data.data.project._etag);
    setSavedJson(JSON.stringify(d));
    const value = (lines: ScheduleLine[]) => money(contractValue(expandTiers(lines.filter((l) => l.type !== "reimbursable"))));
    setConverted(
      old.length
        ? [
            ...old.map((l) => {
              const r = regularized(l);
              const was = l.count === 1 ? `one ${money(l.amount)} payment spread over` : `${l.count} ${l.frequency} payments of ${money(l.amount)}, each spread over`;
              return `${l.label || "A payment"}: ${was} ${l.monthsCovered} months now reads as ${money(r.amount)} ${r.frequency === "monthly" ? "a month" : "a quarter"} for ${coverCount(r)} ${r.frequency === "monthly" ? "months" : "quarters"}.`;
            }),
            `Contract value ${value(d.plan.schedule)} → ${value(next.plan.schedule)}. Save to keep it, or change the lines below.`,
          ]
        : [],
    );
  }, [data.data]); // eslint-disable-line react-hooks/exhaustive-deps

  const dirty = useMemo(() => !!draft && JSON.stringify(draft) !== savedJson, [draft, savedJson]);

  // Unsaved work is never lost silently: leaving the page or the screen asks first.
  useEffect(() => {
    if (!dirty) return;
    const before = (e: BeforeUnloadEvent) => e.preventDefault();
    let last = window.location.hash;
    const onHash = (e: HashChangeEvent) => {
      if (window.confirm("This plan has unsaved changes. Leave without saving?")) return;
      e.stopImmediatePropagation();
      window.history.replaceState(null, "", new URL(e.oldURL).hash || last);
    };
    window.addEventListener("beforeunload", before);
    window.addEventListener("hashchange", onHash, true);
    last = window.location.hash;
    return () => {
      window.removeEventListener("beforeunload", before);
      window.removeEventListener("hashchange", onHash, true);
    };
  }, [dirty]);

  const plan = draft?.plan;
  const load = useApi<LoadResponse>(plan ? `/projects/${encodeURIComponent(id)}/load?start=${plan.startMonday}&weeks=${plan.weeks}` : null);
  const info = data.data;
  const financial = !!info?.access?.financial;
  const editable = !!info?.access?.edit;
  const settings = info?.settings;
  const target = draft?.targetMargin ?? settings?.targetMargin ?? 0.3;
  const holidays = useMemo(() => new Set(info?.holidays ?? []), [info]);
  const rates = useMemo(() => (plan && info?.costRates ? ratesForPlan(plan, info.costRates) : null), [plan, info]);
  const estimate = useMemo(
    () =>
      plan
        ? estimatePlan(plan, rates ?? [], {
            basis,
            targetMargin: target,
            overheadPerHour: burdened ? (settings?.overheadPerHour ?? 0) : 0,
            holidays,
          })
        : null,
    [plan, rates, basis, target, burdened, settings, holidays],
  );

  const save = useCallback(async () => {
    if (!draft || !etag) return;
    setSaving(true);
    setError(undefined);
    try {
      const { project } = await send<{ project: ProjectResponse["project"] }>("PUT", `/projects/${encodeURIComponent(id)}`, {
        etag,
        // A cleared date is sent as null, so the saved one is removed rather than kept.
        project: { ...draft, contractDate: draft.contractDate ?? null, contractStart: draft.contractStart ?? null, contractEnd: draft.contractEnd ?? null },
      });
      forgetCodeLabels();
      const d = draftOf(project);
      setEtag(project._etag);
      setSavedJson(JSON.stringify(d));
      history.reset(d);
    } catch (err) {
      setError(errorText(err));
    }
    setSaving(false);
  }, [draft, etag, id, history]);

  // Ctrl+Z / Ctrl+Y (or Ctrl+Shift+Z) and Ctrl+S, unless the focus is in a text field.
  const frame = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!(e.ctrlKey || e.metaKey) || !editable) return;
      const t = e.target as HTMLElement;
      const inField = t.closest("input, textarea, select, [contenteditable=true]") && !t.closest(".grid-frame");
      const k = e.key.toLowerCase();
      if (k === "s") {
        e.preventDefault();
        if (dirty) void save();
      } else if (!inField && (k === "y" || (k === "z" && e.shiftKey))) {
        e.preventDefault();
        history.redo();
      } else if (!inField && k === "z") {
        e.preventDefault();
        history.undo();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [editable, dirty, save, history]);

  // Mock mode only: what the end-to-end test checks after each interaction (e2e/editor.e2e.mjs).
  useEffect(() => {
    if (!MOCK || !estimate || !draft) return;
    (window as unknown as { __editor?: unknown }).__editor = {
      hours: estimate.hours,
      labor: estimate.labor,
      clientFees: estimate.clientFees,
      contributionPct: estimate.contributionPct,
      dirty,
      weeks: draft.plan.weeks,
      rows: draft.plan.assignments.map((a) => a.hours),
      loaded: !!load.data,
    };
  }, [estimate, draft, dirty, load.data]);

  const onEdits = useCallback(
    (edits: GridEdit[]) =>
      set((d) => {
        const assignments = d.plan.assignments.map((a) => ({ ...a, hours: [...a.hours] }));
        for (const e of edits) for (const w of e.weeks) assignments[e.row].hours[w] = Math.min(80, Math.max(0, Math.round(e.value * 100) / 100));
        return { ...d, plan: { ...d.plan, assignments } };
      }),
    [set],
  );

  if (!info || !draft || !plan || !estimate) {
    return (
      <Page title="Project">
        <BackRow id={id} />
        {data.loading ? <Loading /> : <ErrorBar message={data.error} />}
      </Page>
    );
  }

  const project = info.project;
  const roleName = (rid: string) => info.roles.find((r) => r.id === rid)?.name ?? rid;
  const personName = (pid: string | null) => (pid ? (info.people.find((p) => p.id === pid)?.name ?? "Unknown person") : "Placeholder");
  const unrated = financial
    ? [...new Set(plan.assignments.filter((a) => (!a.personId || !info.access.pay) && info.costRates?.roles[a.roleId]?.unrated).map((a) => roleName(a.roleId)))]
    : [];
  const gaps = coverageWarnings(plan, info, load.data ?? null, personName);
  const applyOp = (op: RangeOp) => block && set((d) => ({ ...d, plan: applyRange(d.plan, block.rows, block.first, block.last, op) }));
  const setPlan = (change: (p: WorkingPlan) => WorkingPlan, key?: string) => set((d) => ({ ...d, plan: change(d.plan) }), key);
  const setField = <K extends keyof Draft>(k: K, v: Draft[K]) => set((d) => ({ ...d, [k]: v }), `field-${String(k)}`);
  const meetsTarget = estimate.contributionPct !== null && estimate.contributionPct >= target - 1e-9;
  const shownPct = burdened ? estimate.burdenedPct : estimate.contributionPct;

  return (
    <div ref={frame}>
      <Page
        title={`${draft.isTemplate ? "Template" : draft.code ? codeLabel(draft.code) : "New project"} — ${draft.name || "Untitled"}`}
        actions={
          <>
            {!draft.isTemplate && (
              <Badge appearance="filled" color={isClosed(project.status) ? "subtle" : project.status === "booked" ? "brand" : "informative"}>
                {STATUS_LABEL[project.status]}
                {project.closedOn ? ` · ${day(project.closedOn)}` : ""}
                {!isClosed(project.status) && project.status !== "booked" ? ` · ${pct(project.probability ?? DEFAULT_PROBABILITY[project.status], 0)}` : ""}
              </Badge>
            )}
            {editable && !draft.isTemplate && (
              <Tooltip content={dirty ? "Save your changes first." : "Move the project through prospect, booked, delivered and so on."} relationship="description">
                <Button disabled={dirty} onClick={() => setDialog({ kind: "status" })}>
                  Change status…
                </Button>
              </Tooltip>
            )}
            {editable && (
              <Button appearance="subtle" disabled={dirty} onClick={() => setDialog({ kind: "delete" })}>
                Delete…
              </Button>
            )}
            {project.renewalOf && (
              <a href={href("projects", project.renewalOf)} className="muted">
                Potential renewal · open the project it renews
              </a>
            )}
            {editable && project.status === "booked" && !draft.isTemplate && (
              <Tooltip content={dirty ? "Save your changes first." : "Copy this project as an opportunity starting when it ends, linked to it for Profitability."} relationship="description">
                <Button disabled={dirty} onClick={() => setDialog({ kind: "renewal" })}>
                  Potential renewal…
                </Button>
              </Tooltip>
            )}
            {editable && (
              <Button disabled={dirty} onClick={() => setDialog({ kind: "copy" })}>
                {draft.isTemplate ? "New project from this…" : "Copy…"}
              </Button>
            )}
            {editable && (
              <>
                <Button disabled={!history.canUndo} onClick={history.undo} aria-label="Undo (Ctrl+Z)">
                  Undo
                </Button>
                <Button disabled={!history.canRedo} onClick={history.redo} aria-label="Redo (Ctrl+Y)">
                  Redo
                </Button>
                <Button appearance="primary" disabled={!dirty || saving} onClick={() => void save()}>
                  {saving ? "Saving…" : dirty ? "Save" : "Saved"}
                </Button>
              </>
            )}
          </>
        }
      >
        <BackRow id={id} />
        <ErrorBar message={error} />
        {!draft.isTemplate && !draft.code && (
          <MessageBar intent="warning">
            <MessageBarBody>This project has no code yet, so reported hours can't map to it.</MessageBarBody>
          </MessageBar>
        )}
        {converted.length > 0 && (
          <MessageBar intent="info" layout="multiline">
            <MessageBarBody>
              <strong>The payment schedule is now entered as an amount each period.</strong>
              <ul className="plain-list">
                {converted.map((c) => (
                  <li key={c}>{c}</li>
                ))}
              </ul>
            </MessageBarBody>
          </MessageBar>
        )}
        {financial && (
          <div className="econ" aria-label="Economics">
            <Kpi label="Price (client fees)" value={money(estimate.clientFees)} sub={estimate.otherRevenue ? `+ ${money(estimate.otherRevenue)} other revenue` : "value-priced"} />
            <Kpi
              label={`Cost (${basis === "base" ? "Base" : basis === "ote" ? "OTE" : basis === "target" ? "target" : "target OTE"} rates)`}
              value={money(estimate.labor + estimate.expenses)}
              sub={
                <span className="seg">
                  <button className={basis === "base" ? "on" : ""} onClick={() => setBasis("base")}>
                    Base
                  </button>
                  <button className={basis === "ote" ? "on" : ""} onClick={() => setBasis("ote")}>
                    OTE
                  </button>
                  <button className={basis === "target" ? "on" : ""} onClick={() => setBasis("target")} title="Each role at its target Base rate, whoever fills it (Settings → Roles)">
                    Target
                  </button>
                  <button className={basis === "targetOte" ? "on" : ""} onClick={() => setBasis("targetOte")} title="Each role at its target OTE rate, whoever fills it (Settings → Roles)">
                    Target OTE
                  </button>
                </span>
              }
            />
            <Kpi
              label={burdened ? "Fully burdened margin" : "Contribution margin"}
              value={shownPct === null ? "—" : pct(shownPct)}
              tone={estimate.contributionPct === null ? undefined : meetsTarget ? "good" : "bad"}
              sub={
                estimate.contributionPct === null
                  ? "add a payment schedule"
                  : `${money(burdened ? estimate.burdened : estimate.contribution)} · ${meetsTarget ? "meets" : "below"} the ${pct(target, 0)} target`
              }
            />
            <Kpi
              label={`Price for ${pct(target, 0)}`}
              value={money(estimate.priceForTarget)}
              sub={
                !estimate.clientFees
                  ? "cost ÷ (1 − target)"
                  : estimate.priceForTarget > estimate.clientFees
                    ? `${money(estimate.priceForTarget - estimate.clientFees)} more than the current price`
                    : `${money(estimate.clientFees - estimate.priceForTarget)} below the current price`
              }
            />
            <Kpi label="Hours" value={Math.round(estimate.hours).toLocaleString("en-US")} sub={`${plan.weeks} weeks to ${day(planEnd(plan))}`} />
            {(settings?.overheadPerHour ?? 0) > 0 && (
              <Switch
                label={`${(settings as { overheadSource?: string }).overheadSource === "shared-costs" ? "Shared costs" : "Overhead"} at ${money(settings!.overheadPerHour)}/h`}
                checked={burdened}
                onChange={(_, d) => setBurdened(d.checked)}
              />
            )}
          </div>
        )}
        {financial && !draft.isTemplate && ["pending", "booked", "delivered", "cancelled"].includes(project.status) && (
          <ActualsToDate projectId={id} from={plan.startMonday} basis={basis} />
        )}
        {gaps.length > 0 && <Warnings items={gaps} />}
        {unrated.length > 0 && (
          <Warnings items={[`${unrated.join(", ")} ${unrated.length === 1 ? "has" : "have"} no standard rate, so ${unrated.length === 1 ? "its" : "their"} rows cost $0. Set an override in Settings → Roles.`]} />
        )}

        <Card className="panel wide">
          <div className="panel-head">
            <Subtitle2>Staffing</Subtitle2>
            <span className="panel-tools">
              <TabList size="small" selectedValue={zoom} onTabSelect={(_, d) => setZoom(d.value as Zoom)} aria-label="Allocation periods">
                <Tab value="week">Weeks</Tab>
                <Tab value="month">Months</Tab>
                <Tab value="quarter">Quarters</Tab>
              </TabList>
            </span>
          </div>
          {editable && (
            <div className="toolbar">
              <Button size="small" onClick={() => setDialog({ kind: "assignment", row: null })}>
                Add assignment
              </Button>
              <span className="divider" />
              <Button size="small" disabled={!block} onClick={() => setDialog({ kind: "range" })}>
                Set, scale, shift or ramp…
              </Button>
              <Button size="small" disabled={!block} onClick={() => applyOp({ kind: "clear" })}>
                Clear
              </Button>
              <span className="divider" />
              <label className="inline-field">
                <Caption1>Starts</Caption1>
                <DateInput
                  size="small"
                  type="date"
                  value={plan.startMonday}
                  onChange={(_, d) => d.value && setPlan((p) => resizePlan(p, mondayOf(d.value), p.weeks))}
                />
              </label>
              <label className="inline-field">
                <Caption1>Weeks</Caption1>
                <Input
                  size="small"
                  type="number"
                  min={1}
                  max={MAX_WEEKS}
                  value={String(plan.weeks)}
                  className="weeks-input"
                  onChange={(_, d) => {
                    const w = Math.round(Number(d.value));
                    if (w >= 1 && w <= MAX_WEEKS) setPlan((p) => resizePlan(p, p.startMonday, w), "weeks");
                  }}
                />
              </label>
            </div>
          )}
          <Caption1 className="muted">
            {editable
              ? `Type hours a week, or select a cell and drag its corner handle to repeat it. Copy and paste work with Excel. Delete clears; Ctrl+Z undoes. ${zoom === "week" ? "" : "In this zoom a cell shows average hours a week, and typing sets every week in it."}`
              : "Read only."}{" "}
            {me.can("comp.read") ? `${editable ? "Click a role to change its assignment, or a name to open that person's card. " : "Click a name to open that person's card. "}` : ""}
            Load rows count {load.data?.counted.join(" and ") ?? "pending and booked"} projects: red over {pct(settings?.loadThresholds.over ?? DEFAULT_THRESHOLDS.over, 0)}, teal on target, blue under{" "}
            {pct(settings?.loadThresholds.under ?? DEFAULT_THRESHOLDS.under, 0)}.
          </Caption1>
          {plan.assignments.length === 0 ? (
            <Body1 className="muted">No one is assigned yet.{editable ? " Start with Add assignment." : ""}</Body1>
          ) : (
            <AllocationGrid
              plan={plan}
              zoom={zoom}
              roleName={roleName}
              personName={personName}
              rates={financial ? rates : null}
              basis={basis}
              estimate={estimate}
              load={load.data ?? null}
              thresholds={settings?.loadThresholds ?? DEFAULT_THRESHOLDS}
              editable={editable}
              onEdits={onEdits}
              onSelect={setBlock}
              onOpenRow={(row) => editable && setDialog({ kind: "assignment", row })}
              onOpenPerson={me.can("comp.read") ? (pid) => go("people", pid) : undefined}
            />
          )}
        </Card>

        {/* P2d tracking sits below the grid: it loads later, and above it would shift the grid mid-edit. */}
        {!draft.isTemplate && ["pending", "booked", "delivered", "cancelled"].includes(project.status) && (
          <ProjectTracking projectId={id} status={project.status} basis={basis} onChanged={() => data.reload()} />
        )}
        {!draft.isTemplate && project.code && <BillingCard code={project.code} />}

        {financial && (
          <div className="stack">
            <ScheduleEditor
              plan={plan}
              editable={editable}
              onChange={(schedule, key) => setPlan((p) => ({ ...p, schedule }), key)}
              estimate={estimate}
              billing={info.clientBilling}
              partnerTerms={info.partnerTerms}
              contractDate={draft.contractDate}
            />
            <ExpenseEditor plan={plan} editable={editable} onChange={(expenses, key) => setPlan((p) => ({ ...p, expenses }), key)} estimate={estimate} />
          </div>
        )}

        {financial && estimate.months.length > 0 && (
          <Card className="panel wide">
            <div className="panel-head">
              <Subtitle2>Margin and cash by month</Subtitle2>
              <Button size="small" appearance="subtle" onClick={() => setShowTable(!showTable)}>
                {showTable ? "Show charts" : "Show as a table"}
              </Button>
            </div>
            {showTable ? (
              <MonthTable months={estimate.months} />
            ) : (
              <div className="card-grid">
                <div>
                  <Caption1>Contribution margin on recognized revenue, by month</Caption1>
                  <MarginChart months={estimate.months} target={target} />
                </div>
                <div>
                  <Caption1>Cumulative cash: invoices in, labor and costs out</Caption1>
                  <CashChart months={estimate.months} />
                </div>
              </div>
            )}
          </Card>
        )}

        <DetailsCard
          draft={draft}
          project={project}
          codes={codes.data?.codes ?? []}
          editable={editable}
          financial={financial}
          settingsTarget={settings?.targetMargin ?? 0.3}
          setField={setField}
          canChangeOwner={!!info.access.owner}
          dirty={dirty}
          onChangeOwner={() => setDialog({ kind: "owner" })}
        />

        {/* CA2: the client's aliases, the same records on every project of this client. */}
        {!project.isTemplate && project.clientName && <ClientAliasesPanel clientName={project.clientName} />}
      </Page>

      {dialog?.kind === "assignment" && (
        <AssignmentDialog
          row={dialog.row}
          plan={plan}
          roles={info.roles}
          people={info.people}
          onClose={() => setDialog(null)}
          onApply={(next) => {
            setPlan(() => next);
            setDialog(null);
          }}
        />
      )}
      {dialog?.kind === "range" && block && (
        <RangeDialog
          block={block}
          plan={plan}
          onClose={() => setDialog(null)}
          onApply={(op) => {
            applyOp(op);
            setDialog(null);
          }}
        />
      )}
      {dialog?.kind === "status" && etag && (
        <StatusDialog
          project={project}
          etag={etag}
          onClose={(changed) => {
            setDialog(null);
            if (changed) data.reload();
          }}
        />
      )}
      {dialog?.kind === "owner" && etag && (
        <OwnerDialog
          project={project}
          etag={etag}
          people={info.people}
          onClose={(changed) => {
            setDialog(null);
            if (changed) data.reload();
          }}
        />
      )}
      {dialog?.kind === "delete" && etag && <DeleteDialog project={project} etag={etag} onClose={() => setDialog(null)} onDeleted={() => go("projects")} />}
      {dialog?.kind === "renewal" && <CopyDialog source={project} renewal codes={codes.data?.codes ?? []} onClose={() => setDialog(null)} onCreated={(newId) => go("projects", newId)} />}
      {dialog?.kind === "copy" && <CopyDialog source={project} codes={codes.data?.codes ?? []} onClose={() => setDialog(null)} onCreated={(newId) => go("projects", newId)} />}
    </div>
  );
}

/** The core check, worded for the editor (see coverageGaps in core/estimate.ts). */
function coverageWarnings(plan: WorkingPlan, info: ProjectResponse, load: LoadResponse | null, personName: (id: string) => string): string[] {
  const weeks = (n: number) => (n === 1 ? "1 week" : `${n} weeks`);
  return coverageGaps(plan, info.access.pay ? info.costRates?.people : undefined, load?.people).map((g) => {
    const parts = [
      g.noPayWeeks ? `${weeks(g.noPayWeeks)} of hours have no pay on file${g.firstPay ? ` (their pay starts ${day(g.firstPay)})` : ""}, so they cost $0` : "",
      g.noCapacityWeeks ? `${weeks(g.noCapacityWeeks)} have no capacity on file, so load shows "no cap."` : "",
    ].filter(Boolean);
    return `${personName(g.personId)}: ${parts.join("; ")}. If they worked for 3Seven then, add their pay, capacity and work state from their real start date on their People card. If not, move the hours.`;
  });
}

// --- Payment schedule (PE7) -------------------------------------------------------------------------

function ScheduleEditor(props: {
  plan: WorkingPlan;
  editable: boolean;
  estimate: ReturnType<typeof estimatePlan>;
  onChange: (s: ScheduleLine[], key?: string) => void;
  billing?: ClientBilling & { set: boolean };
  partnerTerms?: Partial<Record<RevenueType, number>>;
  contractDate?: string;
}) {
  const lines = props.plan.schedule;
  const [allInvoices, setAllInvoices] = useState(false);
  const invoices = scheduledInvoices(lines, props.billing, props.contractDate, props.partnerTerms);
  const terms = props.billing?.termsDays ?? 45;
  // What a line inherits when it has no day of its own: the client's row for its service, else the contract date.
  const inherited = (l: ScheduleLine) => {
    const r = resolveInvoiceDay({ type: l.type }, props.billing, props.contractDate);
    if (r.source !== "client") return props.contractDate ? `Contract date: the ${ordinal(Number(props.contractDate.slice(8, 10)))}` : "Contract date (none yet: the 1st)";
    return `Client's table: ${invoiceDayLabel(r.day, props.contractDate).replace(/^The /, "the ")}`;
  };
  const replace = (i: number, line: ScheduleLine, key: string) => props.onChange(lines.map((l, j) => (j === i ? line : l)), `schedule-${i}-${key}`);
  const update = (i: number, patch: Partial<ScheduleLine>, key: string) => replace(i, { ...lines[i], ...patch }, key);
  const n = (v: string) => (v === "" ? 0 : Math.max(0, Number(v)));
  const payments = (l: ScheduleLine) => (l.frequency === "once" ? 1 : l.count);
  const per = { once: undefined, monthly: "/mo", quarterly: "/qtr" } as const;
  const unit = (l: ScheduleLine) => (l.frequency === "quarterly" ? "quarters" : "months");
  return (
    <Card className="panel">
      <div className="panel-head">
        <Subtitle2>Payment schedule</Subtitle2>
        {props.editable && (
          <Button
            size="small"
            onClick={() =>
              props.onChange([
                ...lines,
                { id: newId(), label: "", amount: 0, frequency: "monthly", count: 1, firstInvoiceMonth: props.contractDate ? props.contractDate.slice(0, 7) : monthOf(props.plan.startMonday), monthsCovered: 1, type: "client-fee" },
              ])
            }
          >
            Add payment
          </Button>
        )}
      </div>
      <Caption1>
        Enter each line as it is stated: an amount each month or quarter, for how many, from the first invoice. A one-time payment
        covers the months of service it pays for. Revenue is recognized over the months each payment pays for; cash lands in each
        invoice month.
      </Caption1>
      {lines.length === 0 ? (
        <Body1 className="muted">No payments yet.</Body1>
      ) : (
        <div className="table-scroll">
          <table className="edit-table">
            <thead>
              <tr>
                <th>Label</th>
                <th>Type</th>
                <th className="num">Amount</th>
                <th>How often</th>
                <th>First invoice</th>
                <th>Invoice day</th>
                <th className="num">Covers</th>
                <th className="num">Total</th>
                <th aria-label="Remove" />
              </tr>
            </thead>
            <tbody>
              {lines.map((l, i) => (
                <tr key={l.id}>
                  <td>
                    <Input size="small" value={l.label ?? ""} disabled={!props.editable} onChange={(_, d) => update(i, { label: d.value }, "label")} aria-label="Label" placeholder="e.g. Mobilization" />
                  </td>
                  <td>
                    <Select size="small" value={l.type ?? "client-fee"} disabled={!props.editable} onChange={(_, d) => update(i, { type: d.value as ScheduleLine["type"] }, "type")} aria-label="Revenue type">
                      {REVENUE_TYPES.map((t) => (
                        <option key={t} value={t}>
                          {REVENUE_TYPE_LABEL[t]}
                        </option>
                      ))}
                    </Select>
                  </td>
                  <td>
                    <Input
                      size="small"
                      type="number"
                      min={0}
                      className="money-input"
                      value={String(l.amount)}
                      disabled={!props.editable}
                      onChange={(_, d) => update(i, { amount: n(d.value) }, "amount")}
                      aria-label={l.frequency === "once" ? "Amount" : `Amount ${l.frequency === "monthly" ? "a month" : "a quarter"}`}
                      contentBefore="$"
                      contentAfter={per[l.frequency]}
                    />
                  </td>
                  <td>
                    <Select size="small" value={l.frequency} disabled={!props.editable} onChange={(_, d) => replace(i, withFrequency(l, d.value as ScheduleLine["frequency"]), "frequency")} aria-label="How often">
                      <option value="once">Once</option>
                      <option value="monthly">Monthly</option>
                      <option value="quarterly">Quarterly</option>
                    </Select>
                  </td>
                  <td>
                    <DateInput size="small" type="month" value={l.firstInvoiceMonth} disabled={!props.editable} onChange={(_, d) => d.value && update(i, { firstInvoiceMonth: d.value }, "first")} aria-label="First invoice month" />
                  </td>
                  <td>
                    <InvoiceDaySelect
                      size="small"
                      value={l.invoiceDay}
                      inherit={inherited(l)}
                      disabled={!props.editable}
                      onChange={(v) => {
                        const { invoiceDay: _, ...rest } = l;
                        replace(i, v === undefined ? rest : { ...rest, invoiceDay: v }, "day");
                      }}
                    />
                  </td>
                  <td className="num">
                    <Input
                      size="small"
                      type="number"
                      min={1}
                      max={l.frequency === "once" ? 36 : 120}
                      className="cover-input"
                      value={String(coverCount(l))}
                      disabled={!props.editable}
                      onChange={(_, d) => replace(i, withCover(l, Math.max(1, Math.round(n(d.value)))), "cover")}
                      aria-label={`Covers how many ${unit(l)}`}
                      contentAfter={unit(l)}
                    />
                  </td>
                  <td className="num">
                    {money(l.amount * payments(l))}
                    {payments(l) > 1 && (
                      <Caption1 className="block muted">
                        {payments(l)} × {money(l.amount)}
                      </Caption1>
                    )}
                  </td>
                  <td>
                    {props.editable && (
                      <Button size="small" appearance="subtle" onClick={() => props.onChange(lines.filter((_, j) => j !== i))}>
                        Remove
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {invoices.length > 0 && (
        <details className="invoices">
          <summary>
            {invoices.length} invoice{invoices.length === 1 ? "" : "s"}, the first on {day(invoices[0].invoiceDate)}; payment expected Net {terms}
            {props.billing?.set ? "" : " (the default: set the client's terms on its page)"}
          </summary>
          <table className="edit-table">
            <thead>
              <tr>
                <th>Invoice date</th>
                <th>Payment</th>
                <th>Service</th>
                <th className="num">Amount</th>
                <th>Payment expected</th>
              </tr>
            </thead>
            <tbody>
              {(allInvoices ? invoices : invoices.slice(0, 12)).map((v, k) => (
                <tr key={`${v.lineId}-${k}`}>
                  <td>{day(v.invoiceDate)}</td>
                  <td>{v.label || REVENUE_TYPE_LABEL[v.type]}</td>
                  <td>
                    {day(v.serviceStart)} – {day(v.serviceEnd)}
                  </td>
                  <td className="num">{money(v.amount)}</td>
                  <td>
                    {day(v.expectedReceipt)}
                    {v.partner && <Caption1 className="block muted">Partner pays, Net {v.termsDays}</Caption1>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {invoices.length > 12 && (
            <Button size="small" appearance="subtle" onClick={() => setAllInvoices(!allInvoices)}>
              {allInvoices ? "Show the first 12" : `Show all ${invoices.length}`}
            </Button>
          )}
          {!props.contractDate && <Caption1 className="block muted">No contract date yet: invoices without a day of their own are dated the 1st.</Caption1>}
        </details>
      )}
      <Body1>
        Contract value <strong>{money(props.estimate.clientFees + props.estimate.otherRevenue)}</strong>
        {props.estimate.otherRevenue ? ` (client fees ${money(props.estimate.clientFees)}, other revenue ${money(props.estimate.otherRevenue)})` : ""}.
      </Body1>
    </Card>
  );
}

// --- Non-labor costs (PE8) --------------------------------------------------------------------------

function ExpenseEditor(props: { plan: WorkingPlan; editable: boolean; estimate: ReturnType<typeof estimatePlan>; onChange: (e: PlanExpense[], key?: string) => void }) {
  const rows = props.plan.expenses;
  const update = (i: number, patch: Partial<PlanExpense>, key: string) => props.onChange(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)), `expense-${i}-${key}`);
  const n = (v: string) => (v === "" ? 0 : Math.max(0, Number(v)));
  return (
    <Card className="panel">
      <div className="panel-head">
        <Subtitle2>Other costs</Subtitle2>
        {props.editable && (
          <Button
            size="small"
            onClick={() => props.onChange([...rows, { id: newId(), category: "travel", label: "", amount: 0, firstMonth: monthOf(props.plan.startMonday), count: 1, reimbursable: false }])}
          >
            Add cost
          </Button>
        )}
      </div>
      <Caption1>Subcontractors, travel, events, software and referral fees. A reimbursable cost is billed back, so it adds equal revenue and cost and never moves margin.</Caption1>
      {rows.length === 0 ? (
        <Body1 className="muted">No other costs.</Body1>
      ) : (
        <div className="table-scroll">
          <table className="edit-table">
            <thead>
              <tr>
                <th>Category</th>
                <th>Label</th>
                <th className="num">Each month</th>
                <th>From</th>
                <th className="num">Months</th>
                <th>Billed back</th>
                <th className="num">Total</th>
                <th aria-label="Remove" />
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={r.id}>
                  <td>
                    <Select size="small" value={r.category} disabled={!props.editable} onChange={(_, d) => update(i, { category: d.value as PlanExpense["category"] }, "category")} aria-label="Category">
                      {EXPENSE_CATEGORIES.map((c) => (
                        <option key={c} value={c}>
                          {EXPENSE_LABEL[c]}
                        </option>
                      ))}
                    </Select>
                  </td>
                  <td>
                    <Input size="small" value={r.label} disabled={!props.editable} onChange={(_, d) => update(i, { label: d.value }, "label")} aria-label="Label" placeholder="e.g. Kickoff travel" />
                  </td>
                  <td>
                    <Input size="small" type="number" min={0} className="money-input" value={String(r.amount)} disabled={!props.editable} onChange={(_, d) => update(i, { amount: n(d.value) }, "amount")} aria-label="Amount each month" contentBefore="$" />
                  </td>
                  <td>
                    <DateInput size="small" type="month" value={r.firstMonth} disabled={!props.editable} onChange={(_, d) => d.value && update(i, { firstMonth: d.value }, "first")} aria-label="First month" />
                  </td>
                  <td>
                    <Input size="small" type="number" min={1} max={120} className="count-input" value={String(r.count)} disabled={!props.editable} onChange={(_, d) => update(i, { count: Math.max(1, Math.round(n(d.value))) }, "count")} aria-label="Months" />
                  </td>
                  <td>
                    <Switch checked={r.reimbursable} disabled={!props.editable} onChange={(_, d) => update(i, { reimbursable: d.checked }, "reimb")} aria-label="Billed back to the client" />
                  </td>
                  <td className="num">{money(r.amount * r.count)}</td>
                  <td>
                    {props.editable && (
                      <Button size="small" appearance="subtle" onClick={() => props.onChange(rows.filter((_, j) => j !== i))}>
                        Remove
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <Body1>
        Costs in margin <strong>{money(props.estimate.expenses)}</strong>
        {props.estimate.reimbursable ? `; billed back ${money(props.estimate.reimbursable)}` : ""}.
      </Body1>
    </Card>
  );
}

// --- Details (PE1) ----------------------------------------------------------------------------------

function DetailsCard(props: {
  draft: Draft;
  project: Project;
  codes: CodesResponse["codes"];
  editable: boolean;
  financial: boolean;
  settingsTarget: number;
  setField: <K extends keyof Draft>(k: K, v: Draft[K]) => void;
  canChangeOwner: boolean;
  dirty: boolean;
  onChangeOwner: () => void;
}) {
  const { draft, setField } = props;
  const canTemplate = draft.isTemplate || ["prospect", "opportunity"].includes(props.project.status);
  return (
    <Card className="panel wide">
      <Subtitle2>Details</Subtitle2>
      <div className="form-grid three">
        {!draft.isTemplate && (
          <Field label="Project code" hint="Reported hours map to the project by this code.">
            <Input
              list="project-codes"
              value={draft.code}
              disabled={!props.editable}
              onChange={(_, d) => {
                const code = d.value.toUpperCase();
                setField("code", code);
                const listed = props.codes.find((c) => c.code === code);
                if (listed && !draft.clientName) setField("clientName", listed.client);
              }}
            />
          </Field>
        )}
        <Field label="Name" required>
          <Input value={draft.name} disabled={!props.editable} onChange={(_, d) => setField("name", d.value)} />
        </Field>
        <Field label="Client">
          <Input value={draft.clientName} disabled={!props.editable} onChange={(_, d) => setField("clientName", d.value)} />
        </Field>
        <Field label="Contract date" hint="Invoices go out on this day of the month, unless the client's invoice table says otherwise.">
          <DateInput value={draft.contractDate ?? ""} disabled={!props.editable} onChange={(_, d) => setField("contractDate", d.value || undefined)} />
        </Field>
        {/* Hours can go to the project only between these days, while it is booked (Chris, 2026-10-28). */}
        <Field label="Contract starts" hint={draft.contractStart ? "No hours can be coded to this project before this day." : `Not set: the plan's start, ${day(draft.plan.startMonday)}, stands in.`}>
          <DateInput value={draft.contractStart ?? ""} disabled={!props.editable} onChange={(_, d) => setField("contractStart", d.value || undefined)} />
        </Field>
        <Field
          label="Contract ends"
          hint={draft.contractEnd ? "No hours can be coded to this project after this day." : `Not set: the plan's last day, ${day(planEnd(draft.plan))}, stands in.`}
          validationMessage={draft.contractStart && draft.contractEnd && draft.contractEnd < draft.contractStart ? "The contract can't end before it starts." : undefined}
        >
          <DateInput value={draft.contractEnd ?? ""} disabled={!props.editable} onChange={(_, d) => setField("contractEnd", d.value || undefined)} />
        </Field>
        <Field label="Offering" hint="e.g. Jump Start, AMaaS. Linked to the offerings catalog in P3.">
          <Input value={draft.offering ?? ""} disabled={!props.editable} onChange={(_, d) => setField("offering", d.value || undefined)} />
        </Field>
        {props.financial && (
          <Field label="Target margin" hint={`Leave empty for the company target, ${pct(props.settingsTarget, 0)}.`}>
            <Input
              type="number"
              min={0}
              max={99}
              contentAfter="%"
              value={draft.targetMargin === undefined ? "" : String(Math.round(draft.targetMargin * 1000) / 10)}
              disabled={!props.editable}
              onChange={(_, d) => setField("targetMargin", d.value === "" ? undefined : Math.min(0.99, Math.max(0, Number(d.value) / 100)))}
            />
          </Field>
        )}
        <Field label="Owner">
          <div className="inline-field">
            <Input value={props.project.ownerName ?? "—"} disabled />
            {props.canChangeOwner && (
              <Tooltip content={props.dirty ? "Save your changes first." : "Hand the project to someone else. The change is recorded."} relationship="description">
                <Button disabled={props.dirty} onClick={props.onChangeOwner}>
                  Change owner…
                </Button>
              </Tooltip>
            )}
          </div>
        </Field>
        <Switch label="Billable" checked={draft.billable} disabled={!props.editable} onChange={(_, d) => setField("billable", d.checked)} />
        <Tooltip content={canTemplate ? "Templates are left out of capacity, pipeline and financials." : "Only a prospect or opportunity can become a template. Copy a booked project instead."} relationship="description">
          <Switch label="This is a template" checked={draft.isTemplate} disabled={!props.editable || !canTemplate} onChange={(_, d) => setField("isTemplate", d.checked)} />
        </Tooltip>
      </div>
      <Field label="Notes">
        <Textarea value={draft.notes ?? ""} disabled={!props.editable} onChange={(_, d) => setField("notes", d.value || undefined)} resize="vertical" />
      </Field>
      <datalist id="project-codes">
        {props.codes.filter((c) => !c.retired).map((c) => (
          <option key={c.code} value={c.code}>
            {c.client}
          </option>
        ))}
      </datalist>
      {props.project.statusHistory.length > 0 && (
        <Caption1>
          History:{" "}
          {props.project.statusHistory
            .slice(-4)
            .map((s) => `${STATUS_LABEL[s.status]} ${day(s.at.slice(0, 10))} by ${s.by}${s.reason ? ` (${s.reason})` : ""}`)
            .join(" · ")}
          {props.project.baselines > 0 ? ` · ${props.project.baselines} baseline${props.project.baselines === 1 ? "" : "s"} taken` : ""}
        </Caption1>
      )}
      {(props.project.ownerHistory?.length ?? 0) > 1 && (
        <Caption1>
          Owners:{" "}
          {props.project
            .ownerHistory!.map((o, i) => `${o.name} from ${day(o.at.slice(0, 10))}${i ? ` (set by ${o.by}${o.note ? `: ${o.note}` : ""})` : ""}`)
            .join(" · ")}
        </Caption1>
      )}
    </Card>
  );
}


/**
 * Actuals to date (PP9, rev. 1.16): reported hours on the project so far, and the margin they give at
 * actual cost (each person's actual rate for the month), with a switch to calculated cost.
 */
function ActualsToDate({ projectId, from, basis }: { projectId: string; from: string; basis: CostBasis }) {
  const [view, setView] = useState<"actual" | "calculated">("actual");
  const to = today();
  const perf = useApi<{
    totals: { planHours: number; actualHours: number; actualCost: number; calculatedCost: number; actualMargin: number | null; calculatedMargin: number | null; actualMarginPct: number | null; calculatedMarginPct: number | null; actualRevenue: number };
    periods: { actualHours: number; planHours: number; planMargin: number; planRevenue: number }[];
    actualBasis?: "paid" | "calculated" | "target";
  }>(from <= to ? `/performance?by=project&id=${encodeURIComponent(projectId)}&from=${from}&to=${to}&zoom=month&basis=${basis}` : null);
  const d = perf.data;
  if (!d || !d.totals.actualHours) return null;
  const t = d.totals;
  const atTarget = basis === "target" || basis === "targetOte";
  const actual = view === "actual" && !atTarget;
  const reported = d.periods.filter((p) => p.actualHours > 0);
  const planHours = reported.reduce((s, p) => s + p.planHours, 0);
  const planRev = reported.reduce((s, p) => s + p.planRevenue, 0);
  const planPct = planRev ? reported.reduce((s, p) => s + p.planMargin, 0) / planRev : null;
  const pctShown = actual ? t.actualMarginPct : t.calculatedMarginPct;
  return (
    <div className="econ" aria-label="Actuals to date">
      <Kpi label="Reported hours to date" value={Math.round(t.actualHours).toLocaleString("en-US")} sub={`against ${Math.round(planHours).toLocaleString("en-US")} planned for those months`} />
      <Kpi
        label={atTarget ? "Cost to date, target rates" : actual ? "Actual cost to date" : "Calculated cost to date"}
        value={money(actual ? t.actualCost : t.calculatedCost)}
        sub={
          atTarget ? (
            "every hour at its role's target"
          ) : (
            <span className="seg">
              <button className={view === "actual" ? "on" : ""} onClick={() => setView("actual")} title="What each person actually cost that month, over the hours they reported">
                Actual
              </button>
              <button className={view === "calculated" ? "on" : ""} onClick={() => setView("calculated")} title="Hours at each person's calculated rate">
                Calculated
              </button>
            </span>
          )
        }
      />
      <Kpi
        label="Margin to date"
        value={pctShown == null ? "—" : pct(pctShown)}
        tone={pctShown == null || planPct == null ? undefined : pctShown >= planPct - 1e-9 ? "good" : "bad"}
        sub={`against ${planPct == null ? "—" : pct(planPct)} planned for those months${actual && t.calculatedMarginPct != null ? `; ${pct(t.calculatedMarginPct)} at calculated cost` : ""}`}
      />
      {actual && d.actualBasis === "calculated" && <Caption1 className="muted">Actual cost needs pay visibility; shown at calculated role rates.</Caption1>}
    </div>
  );
}
